import { type ErrorEvent, type init } from '@sentry/react';

import { stripQuery } from '@apps/web/lib/strip_query';

// `@sentry/react` v10 does not re-export the `TransactionEvent` type, so derive
// it from the public `init` options' `beforeSendTransaction` parameter.
export type SentryTransactionEvent = Parameters<
    NonNullable<Parameters<typeof init>[0]['beforeSendTransaction']>
>[0];

// Known-benign browser/extension lifecycle messages. These are environmental
// races -- tab teardown, service-worker eviction, message-port disconnects --
// not application defects. They carry no actionable stack and inflate error
// rates. Matched as substrings because the surrounding prefix/suffix varies.
const BENIGN_LIFECYCLE_ERROR_FRAGMENTS: ReadonlyArray<string> = [
    'The browser is shutting down.',
    'No current window',
    'No window with id',
    'No tab with id',
    'Extension context invalidated',
    'Could not establish connection. Receiving end does not exist.',
    'The message port closed before a response was received.',
    'A listener indicated an asynchronous response by returning true, but the message channel closed',
];

// A dynamic route chunk whose fetch fails or is aborted. Each engine words it
// differently; WebKit's phrasing dominates because iOS Safari surfaces the
// abort that fires when a page load loses connectivity or navigates away.
const MODULE_IMPORT_ERROR_FRAGMENTS: ReadonlyArray<string> = [
    'Importing a module script failed.',
    'Failed to fetch dynamically imported module',
    'error loading dynamically imported module',
];

const INJECTED_DOM_SERIALIZATION_ERROR_FRAGMENTS: ReadonlyArray<string> = [
    'Converting circular structure to JSON',
    "object with constructor 'HTMLAnchorElement'",
    "property '__reactFiber$",
    "property 'stateNode' closes the circle",
];

export function isBenignLifecycleError(event: ErrorEvent): boolean {
    const values = event.exception?.values ?? [];
    const primaryExceptionValue =
        values.length > 0 ? values[values.length - 1]?.value : undefined;
    const candidates = [event.message, primaryExceptionValue].filter(
        (text): text is string => typeof text === 'string',
    );
    return candidates.some((text) =>
        BENIGN_LIFECYCLE_ERROR_FRAGMENTS.some((fragment) =>
            text.includes(fragment),
        ),
    );
}

function isInjectedDomSerializationError(event: ErrorEvent): boolean {
    if (event.transaction !== '/sign-in') {
        return false;
    }

    const values = event.exception?.values ?? [];
    const primaryException =
        values.length > 0 ? values[values.length - 1] : undefined;
    const value = primaryException?.value;
    if (
        typeof value !== 'string' ||
        !INJECTED_DOM_SERIALIZATION_ERROR_FRAGMENTS.every((fragment) =>
            value.includes(fragment),
        )
    ) {
        return false;
    }

    // Email-security scanners can inject an appendChild wrapper that tries to
    // serialize React-managed DOM nodes. Require its anonymous frame so a real
    // application serialization bug with the same message still reaches us.
    return (
        primaryException?.stacktrace?.frames?.some(
            (frame) =>
                frame.filename === '<anonymous>' &&
                frame.function?.endsWith('.appendChild'),
        ) ?? false
    );
}

// A failed chunk load rejects the import promise, which TanStack Router turns
// into a rendered error. `index.html` reloads the page on the same failure, so
// that rendered error is never seen -- report it only when the reload was
// throttled and the user was actually left looking at the error boundary.
function isSelfHealingModuleImportError(event: ErrorEvent): boolean {
    if (!window.__phoebeChunkReloadPending) {
        return false;
    }

    const values = event.exception?.values ?? [];
    const value = values.length > 0 ? values[values.length - 1]?.value : null;
    return (
        typeof value === 'string' &&
        MODULE_IMPORT_ERROR_FRAGMENTS.some((fragment) =>
            value.includes(fragment),
        )
    );
}

export function shouldDropSentryError(event: ErrorEvent): boolean {
    return (
        isBenignLifecycleError(event) ||
        isInjectedDomSerializationError(event) ||
        isSelfHealingModuleImportError(event)
    );
}

// Span data keys whose values are full or path-relative URLs we can path-strip
// while keeping the endpoint path.
const URL_SPAN_DATA_KEYS = ['http.url', 'url', 'http.target'] as const;

// Sentry browser tracing records the transaction name, the request URL, and a
// URL on each fetch/XHR span. Our API client serializes user-entered `search`
// terms (e.g. a client or caregiver name) into query params, so strip query
// strings from every URL before a transaction leaves the browser. The endpoint
// path alone is enough for per-endpoint latency.
export function scrubTransactionEventUrls(
    event: SentryTransactionEvent,
): SentryTransactionEvent {
    if (event.transaction) {
        event.transaction = stripQuery(event.transaction);
    }
    if (event.request?.url) {
        event.request.url = stripQuery(event.request.url);
    }
    event.spans?.forEach((span) => {
        if (span.description) {
            span.description = stripQuery(span.description);
        }
        for (const key of URL_SPAN_DATA_KEYS) {
            const value = span.data[key];
            if (typeof value === 'string') {
                span.data[key] = stripQuery(value);
            }
        }
        // `http.query` holds only the query string (there is no path to keep),
        // so drop it wholesale rather than path-stripping.
        if (typeof span.data['http.query'] === 'string') {
            span.data['http.query'] = '';
        }
    });
    return event;
}
